import type { AppState, ShoppingItem } from '../types';
import { newId } from './ids';

const SAFETY_KEYWORDS: Record<string, string> = {
  grape: 'halve lengthwise',
  grapes: 'halve lengthwise',
  'cherry tomato': 'quarter',
  'cherry tomatoes': 'quarter',
  'grape tomato': 'quarter',
  'grape tomatoes': 'quarter',
  'raw carrot': 'shred or steam tender',
  'raw carrots': 'shred or steam tender',
  'hot dog': 'quarter lengthwise then dice (skip until age 4)',
  'hot dogs': 'quarter lengthwise then dice (skip until age 4)',
  'whole nut': 'do not serve',
  'whole nuts': 'do not serve',
  popcorn: 'do not serve',
  'sunbutter': 'spread thin (never in chunks)',
  'peanut butter': 'spread thin (never in chunks)',
  raisin: 'soft and chopped if served',
  raisins: 'soft and chopped if served',
};

function findSafetyMod(ingredientName: string, recipeNotes: string[]): string | null {
  const name = ingredientName.trim().toLowerCase();
  // Prefer recipe-specific note that mentions the ingredient
  for (const note of recipeNotes) {
    if (note.toLowerCase().includes(name)) {
      // Remove the ingredient name from the note (wherever it appears) — keep the action
      const cleaned = note.replace(new RegExp(`\\s*${name}\\s*`, 'i'), ' ').trim();
      return cleaned || note;
    }
  }
  // Fall back to keyword dictionary
  for (const key of Object.keys(SAFETY_KEYWORDS)) {
    if (name.includes(key)) return SAFETY_KEYWORDS[key]!;
  }
  return null;
}

export function buildShoppingList(state: AppState, weekId: string): ShoppingItem[] {
  const week = state.weeks.find(w => w.id === weekId);
  if (!week) return [];

  type Acc = {
    name: string;
    quantity_hints: string[];
    grocery_section: ShoppingItem['grocery_section'];
    recipes: Set<string>;
    safety: string | null;
  };
  const map = new Map<string, Acc>();

  for (const m of week.meals) {
    if (!m.recipe_id) continue;
    const r = state.recipes.find(x => x.id === m.recipe_id);
    if (!r) continue;
    for (const ing of r.ingredients) {
      const key = ing.name.trim().toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.recipes.add(r.id);
        if (ing.quantity) existing.quantity_hints.push(ing.quantity);
        if (!existing.safety) existing.safety = findSafetyMod(ing.name, r.safety_notes);
      } else {
        map.set(key, {
          name: ing.name.trim().toLowerCase(),
          quantity_hints: ing.quantity ? [ing.quantity] : [],
          grocery_section: ing.grocery_section,
          recipes: new Set([r.id]),
          safety: findSafetyMod(ing.name, r.safety_notes),
        });
      }
    }
  }

  const items: ShoppingItem[] = Array.from(map.values()).map(acc => ({
    id: newId(),
    name: acc.name,
    quantity_hint: acc.quantity_hints.join(', '),
    grocery_section: acc.grocery_section,
    used_in_recipes: Array.from(acc.recipes),
    safety_modification: acc.safety,
    bulk_candidate: acc.recipes.size >= 3,
    checked: false,
    checked_at: null,
    added_manually: false,
  }));

  return items.sort((a, b) =>
    a.grocery_section.localeCompare(b.grocery_section) || a.name.localeCompare(b.name)
  );
}

export function refreshShoppingList(
  state: AppState,
  weekId: string,
  previous: ShoppingItem[],
): ShoppingItem[] {
  const fresh = buildShoppingList(state, weekId);
  const previousByName = new Map(previous.map(p => [p.name.toLowerCase(), p]));
  const merged = fresh.map(item => {
    const prior = previousByName.get(item.name.toLowerCase());
    if (prior) {
      return { ...item, id: prior.id, checked: prior.checked, checked_at: prior.checked_at };
    }
    return item;
  });
  // Re-add manual items not present in fresh
  const freshNames = new Set(fresh.map(f => f.name.toLowerCase()));
  for (const p of previous) {
    if (p.added_manually && !freshNames.has(p.name.toLowerCase())) {
      merged.push(p);
    }
  }
  return merged;
}
